import { useApolloClient } from '@apollo/client';
import {
  BooleanDotRenderer,
  type Column,
  createDateRangeFilterValidators,
  DateRenderer,
  type ExplorerDataProvider,
  filterToPostGraphileFilter,
  FilterTypes,
  NavigationExplorer,
  sortToPostGraphileOrderBy,
  transformRange,
} from '@axinom/mosaic-ui';
import React from 'react';
import {
  type TenantAdminFilter,
  TenantAdminsOrderBy,
  TenantAdminsWithDataDocument,
  type TenantAdminsWithDataQuery,
  type TenantAdminsWithDataQueryVariables,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';

type TenantAdminData = NonNullable<
  TenantAdminsWithDataQuery['filtered']
>['nodes'][number];

const explorerColumns: Column<TenantAdminData>[] = [
  { label: 'Name', propertyName: 'name' },
  {
    label: 'Email',
    propertyName: 'email',
  },
  {
    label: 'Status',
    propertyName: 'active',
    render: BooleanDotRenderer,
    tooltip: false,
  },
  { label: 'Created At', propertyName: 'createdAt', render: DateRenderer },
  { label: 'Created By', propertyName: 'createdBy' },
  {
    label: 'Last Modified At',
    propertyName: 'updatedAt',
    render: DateRenderer,
  },
  { label: 'Last Modified By', propertyName: 'updatedBy' },
];

export const TenantAdministrators: React.FC = () => {
  const apolloClient = useApolloClient();

  const dataProvider: ExplorerDataProvider<TenantAdminData> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const result = await apolloClient.query<
        TenantAdminsWithDataQuery,
        TenantAdminsWithDataQueryVariables
      >({
        query: TenantAdminsWithDataDocument,
        variables: {
          filter: filterToPostGraphileFilter<TenantAdminFilter>(filters, {
            name: 'includesInsensitive',
            email: 'includesInsensitive',
            active: 'in',
            createdAt: transformRange,
            createdBy: 'includesInsensitive',
            updatedAt: transformRange,
            updatedBy: 'includesInsensitive',
          }),
          orderBy: sortToPostGraphileOrderBy(sorting, TenantAdminsOrderBy),
          after: pagingInformation,
        },
        fetchPolicy: 'network-only',
      });

      const data = result.data.filtered?.nodes ?? [];

      return {
        data,
        totalCount: result.data.nonFiltered?.totalCount ?? 0,
        filteredCount: result.data.filtered?.totalCount ?? 0,
        hasMoreData: result.data.filtered?.pageInfo.hasNextPage ?? false,
        pagingInformation: result.data.filtered?.pageInfo.endCursor,
      };
    },
  };

  const [createFromDateFilterValidator, createToDateFilterValidator] =
    createDateRangeFilterValidators<TenantAdminData>();

  return (
    <div>
      <NavigationExplorer<TenantAdminData>
        title="Administrators"
        stationKey="ServicePackagesExplorer"
        columns={explorerColumns}
        dataProvider={dataProvider}
        calculateNavigateUrl={(item) => `/tenantAdmin/${item.id}`}
        onCreateAction={`/tenantAdmin/create/`}
        defaultSortOrder={{ column: 'updatedAt', direction: 'desc' }}
        filterOptions={[
          {
            label: 'Name',
            property: 'name',
            type: FilterTypes.FreeText,
          },
          {
            label: 'Email',
            property: 'email',
            type: FilterTypes.FreeText,
          },
          {
            label: 'Status',
            property: 'active',
            type: FilterTypes.Options,
            options: [
              {
                label: 'Active',
                value: true,
              },
              {
                label: 'Blocked',
                value: false,
              },
            ],
          },
          {
            label: 'Creation Period (From)',
            property: 'createdAt',
            type: FilterTypes.Date,
            onValidate: createFromDateFilterValidator('createdAt'),
          },
          {
            label: 'Creation Period (To)',
            property: 'createdAt',
            type: FilterTypes.Date,
            onValidate: createToDateFilterValidator('createdAt'),
          },
          {
            label: 'Created By',
            property: 'createdBy',
            type: FilterTypes.FreeText,
          },
          {
            label: 'Modification Period (From)',
            property: 'updatedAt',
            type: FilterTypes.Date,
            onValidate: createFromDateFilterValidator('updatedAt'),
          },
          {
            label: 'Modification Period (To)',
            property: 'updatedAt',
            type: FilterTypes.Date,
            onValidate: createToDateFilterValidator('updatedAt'),
          },
          {
            label: 'Last Modified By',
            property: 'updatedBy',
            type: FilterTypes.FreeText,
          },
        ]}
      />
    </div>
  );
};

export const TenantAdministratorsCrumb: BreadcrumbResolver = () =>
  'Administrators';
